import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from "recharts";
import Layout from "@/components/Layout";
import EntryForm from "@/components/EntryForm";
import TrainingForm from "@/components/TrainingForm";
import PlayerForm from "@/components/PlayerForm";
import InviteDialog from "@/components/InviteDialog";
import { api, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRAINING_FIELDS } from "@/lib/trainingFields";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, Users, Ruler, Weight, Flag, Footprints, Calendar, Timer, UserPlus,
} from "lucide-react";

const METRICS = [
  { k: "goals", l: "Goals", color: "#15803D" },
  { k: "assists", l: "Assists", color: "#007AFF" },
  { k: "minutes", l: "Minutes", color: "#34C759" },
  { k: "sprint_speed", l: "Sprint Speed", color: "#FF9500" },
  { k: "fitness_score", l: "Fitness", color: "#34C759" },
  { k: "distance_km", l: "Distance", color: "#007AFF" },
  { k: "passing", l: "Passing", color: "#15803D" },
  { k: "shooting", l: "Shooting", color: "#FF9500" },
  { k: "dribbling", l: "Dribbling", color: "#007AFF" },
  { k: "defending", l: "Defending", color: "#34C759" },
  { k: "stamina", l: "Stamina", color: "#15803D" },
];
const SKILLS = ["passing", "shooting", "dribbling", "defending", "stamina"];

function StatBox({ label, value, sub }) {
  return (
    <div className="bg-[#141414] border border-[#27272A] p-5">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A]">{label}</p>
      <p className="font-mono-metric text-3xl font-bold mt-2 text-white">{value}</p>
      {sub && <p className="text-xs text-[#71717A] mt-1">{sub}</p>}
    </div>
  );
}

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [player, setPlayer] = useState(null);
  const [entries, setEntries] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [showEntry, setShowEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showTraining, setShowTraining] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [trainMetric, setTrainMetric] = useState("sprint_20yd");
  const [showEdit, setShowEdit] = useState(false);
  const [showBio, setShowBio] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const openNewEntry = () => {
    setEditingEntry(null);
    setShowEntry(true);
  };
  const openEditEntry = (e) => {
    setEditingEntry(e);
    setShowEntry(true);
  };
  const openNewTraining = () => {
    setEditingTraining(null);
    setShowTraining(true);
  };
  const openEditTraining = (t) => {
    setEditingTraining(t);
    setShowTraining(true);
  };
  const [metric, setMetric] = useState("goals");

  const load = () => {
    api.get(`/players/${id}`).then((r) => setPlayer(r.data)).catch(() => navigate("/", { replace: true }));
    api.get(`/players/${id}/entries`).then((r) => setEntries(r.data)).catch(() => {});
    api.get(`/players/${id}/training`).then((r) => setTrainings(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  const totals = useMemo(() => {
    const sum = (k) => entries.reduce((a, e) => a + (e[k] || 0), 0);
    const avg = (k) => {
      const vals = entries.map((e) => e[k]).filter((v) => v != null);
      return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
    };
    return {
      goals: sum("goals"), assists: sum("assists"), apps: entries.length,
      minutes: sum("minutes"), fitness: avg("fitness_score"), sprint: avg("sprint_speed"),
      yellow: sum("yellow_cards"), red: sum("red_cards"),
    };
  }, [entries]);

  const chartData = useMemo(
    () => entries.map((e) => ({ date: e.date?.slice(5), value: e[metric] })).filter((d) => d.value != null),
    [entries, metric]
  );

  const radarData = useMemo(() => {
    const last = entries[entries.length - 1] || {};
    return SKILLS.map((s) => ({ skill: s[0].toUpperCase() + s.slice(1), value: last[s] || 0 }));
  }, [entries]);

  const deletePlayer = async () => {
    await api.delete(`/players/${id}`);
    toast.success("Player removed");
    navigate("/");
  };

  const deleteEntry = async (eid) => {
    await api.delete(`/players/${id}/entries/${eid}`);
    toast.success("Entry deleted");
    load();
  };

  const deleteTraining = async (tid) => {
    await api.delete(`/players/${id}/training/${tid}`);
    toast.success("Training test deleted");
    load();
  };

  const trainField = TRAINING_FIELDS.find((f) => f.k === trainMetric) || TRAINING_FIELDS[0];
  const trainChartData = useMemo(
    () => trainings.map((t) => ({ date: t.date?.slice(5), value: t[trainMetric] })).filter((d) => d.value != null),
    [trainings, trainMetric]
  );
  const trainBest = useMemo(() => {
    const best = {};
    TRAINING_FIELDS.forEach(({ k, lowerBetter }) => {
      const vals = trainings.map((t) => t[k]).filter((v) => v != null);
      if (vals.length) best[k] = lowerBetter ? Math.min(...vals) : Math.max(...vals);
    });
    return best;
  }, [trainings]);

  if (!player)
    return (
      <Layout>
        <div className="h-64 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#15803D] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );

  const metricColor = METRICS.find((m) => m.k === metric)?.color || "#15803D";

  return (
    <Layout>
      {isAdmin && (
        <Button
          data-testid="back-to-roster"
          variant="ghost"
          onClick={() => navigate("/")}
          className="rounded-none text-[#A1A1AA] hover:text-white mb-6 pl-0 hover:bg-transparent"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to roster
        </Button>
      )}

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-[#141414] border border-[#27272A] overflow-hidden mb-6"
      >
        <div className="flex flex-col md:flex-row">
          <div className="relative w-full md:w-72 h-72 bg-[#1A1A1A] shrink-0">
            {player.photo_url ? (
              <img src={resolveImg(player.photo_url)} alt={player.name} className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#3f3f46]"><Users className="w-20 h-20" /></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#141414] hidden md:block" />
            <span className="absolute top-2 left-4 font-display text-8xl font-black text-white/15 leading-none">{player.jersey_number}</span>
          </div>
          <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 bg-[#15803D] text-white">{player.position}</span>
              <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-none mt-3">{player.name}</h1>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm text-[#A1A1AA]">
                {player.age && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#71717A]" />{player.age} yrs</span>}
                {player.height_cm && <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4 text-[#71717A]" />{player.height_cm} cm</span>}
                {player.weight_kg && <span className="flex items-center gap-1.5"><Weight className="w-4 h-4 text-[#71717A]" />{player.weight_kg} kg</span>}
                {player.preferred_foot && <span className="flex items-center gap-1.5"><Footprints className="w-4 h-4 text-[#71717A]" />{player.preferred_foot}</span>}
                {player.nationality && <span className="flex items-center gap-1.5"><Flag className="w-4 h-4 text-[#71717A]" />{player.nationality}</span>}
              </div>
              {player.notes && <p className="text-sm text-[#71717A] mt-4 max-w-xl">{player.notes}</p>}
            </div>
            <div className="flex gap-3 mt-6">
              {isAdmin ? (
                <>
                  <Button data-testid="edit-player-button" onClick={() => setShowEdit(true)} variant="outline" className="rounded-none border-[#27272A] hover:border-white/30 hover:bg-[#1A1A1A]">
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button data-testid="invite-player-button" onClick={() => setShowInvite(true)} variant="outline" className="rounded-none border-[#27272A] text-[#007AFF] hover:bg-[#007AFF]/10 hover:border-[#007AFF]">
                    <UserPlus className="w-4 h-4 mr-2" /> Invite
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid="delete-player-button" variant="outline" className="rounded-none border-[#27272A] text-red-500 hover:bg-red-500/10 hover:border-red-500">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#141414] border-[#27272A] rounded-none">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-display uppercase">Delete {player.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This removes the player, their login and all logged data. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="confirm-delete-player" onClick={deletePlayer} className="rounded-none bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <Button data-testid="edit-profile-button" onClick={() => setShowBio(true)} variant="outline" className="rounded-none border-[#27272A] hover:border-white/30 hover:bg-[#1A1A1A]">
                  <Pencil className="w-4 h-4 mr-2" /> Edit My Profile
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatBox label="Goals" value={totals.goals} />
        <StatBox label="Assists" value={totals.assists} />
        <StatBox label="Appearances" value={totals.apps} />
        <StatBox label="Minutes" value={totals.minutes} />
        <StatBox label="Avg Fitness" value={totals.fitness ? totals.fitness.toFixed(0) : "—"} sub="/ 100" />
        <StatBox label="Avg Sprint" value={totals.sprint ? totals.sprint.toFixed(1) : "—"} sub="km/h" />
        <StatBox label="Yellow Cards" value={totals.yellow} />
        <StatBox label="Red Cards" value={totals.red} />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-3xl font-black uppercase tracking-tight">Performance</h2>
        {isAdmin && (
          <Button data-testid="log-entry-button" onClick={openNewEntry} className="rounded-none bg-[#15803D] hover:bg-[#166534] font-bold uppercase tracking-wider">
            <Plus className="w-4 h-4 mr-1.5" /> Log Data
          </Button>
        )}
      </div>

      <Tabs defaultValue="charts" className="w-full">
        <TabsList className="rounded-none bg-[#141414] border border-[#27272A] p-1">
          <TabsTrigger value="charts" data-testid="tab-charts" className="rounded-none data-[state=active]:bg-[#15803D] data-[state=active]:text-white uppercase text-xs font-bold tracking-wider">Charts</TabsTrigger>
          <TabsTrigger value="table" data-testid="tab-table" className="rounded-none data-[state=active]:bg-[#15803D] data-[state=active]:text-white uppercase text-xs font-bold tracking-wider">Data Log</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training" className="rounded-none data-[state=active]:bg-[#15803D] data-[state=active]:text-white uppercase text-xs font-bold tracking-wider">Training Lab</TabsTrigger>
        </TabsList>

        <TabsContent value="charts" className="mt-4">
          {entries.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#27272A]">
              <p className="text-[#A1A1AA] font-display text-2xl uppercase font-bold">No data logged</p>
              <p className="text-[#71717A] text-sm mt-1">Log a session to see progress charts.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-[#141414] border border-[#27272A] p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A]">Metric Over Time</p>
                  <Select value={metric} onValueChange={setMetric}>
                    <SelectTrigger data-testid="metric-select" className="w-44 h-9 rounded-none bg-[#0A0A0A] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#27272A] rounded-none max-h-72">
                      {METRICS.map((m) => <SelectItem key={m.k} value={m.k}>{m.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                    <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 0, color: "#fff" }} />
                    <Line type="monotone" dataKey="value" stroke={metricColor} strokeWidth={2.5} dot={{ r: 3, fill: metricColor }} activeDot={{ r: 5 }} isAnimationActive />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-[#141414] border border-[#27272A] p-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A] mb-4">Skill Profile (latest)</p>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData} outerRadius={90}>
                    <PolarGrid stroke="#27272A" />
                    <PolarAngleAxis dataKey="skill" tick={{ fill: "#A1A1AA", fontSize: 11 }} />
                    <Radar dataKey="value" stroke="#15803D" fill="#15803D" fillOpacity={0.35} isAnimationActive />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="table" className="mt-4">
          <div className="bg-[#141414] border border-[#27272A] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#27272A] hover:bg-transparent">
                  {["Date", "Type", "Opp", "G", "A", "Min", "Sprint", "Fit", "Pas", "Sho", "Dri", "Def", "Sta", ...(isAdmin ? [""] : [])].map((h, hi) => (
                    <TableHead key={`${h}-${hi}`} className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow><TableCell colSpan={14} className="text-center text-[#71717A] py-10">No entries yet.</TableCell></TableRow>
                ) : (
                  [...entries].reverse().map((e) => (
                    <TableRow key={e.id} data-testid={`entry-row-${e.id}`} className="border-[#27272A] font-mono-metric text-sm hover:bg-[#0A0A0A]">
                      <TableCell className="whitespace-nowrap font-sans">{e.date}</TableCell>
                      <TableCell className="font-sans">{e.entry_type}</TableCell>
                      <TableCell className="font-sans text-[#A1A1AA]">{e.opponent || "—"}</TableCell>
                      <TableCell>{e.goals ?? 0}</TableCell>
                      <TableCell>{e.assists ?? 0}</TableCell>
                      <TableCell>{e.minutes ?? 0}</TableCell>
                      <TableCell>{e.sprint_speed ?? "—"}</TableCell>
                      <TableCell>{e.fitness_score ?? "—"}</TableCell>
                      <TableCell>{e.passing ?? "—"}</TableCell>
                      <TableCell>{e.shooting ?? "—"}</TableCell>
                      <TableCell>{e.dribbling ?? "—"}</TableCell>
                      <TableCell>{e.defending ?? "—"}</TableCell>
                      <TableCell>{e.stamina ?? "—"}</TableCell>
                      {isAdmin && (
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <button data-testid={`edit-entry-${e.id}`} onClick={() => openEditEntry(e)} className="text-[#71717A] hover:text-[#007AFF] transition-colors">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button data-testid={`delete-entry-${e.id}`} onClick={() => deleteEntry(e.id)} className="text-[#71717A] hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="training" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <Timer className="w-5 h-5 text-[#15803D]" />
              <p className="text-sm">Combine & athletic testing — sprints, agility drills, jumps and more.</p>
            </div>
            {isAdmin && (
              <Button data-testid="log-training-button" onClick={openNewTraining} className="rounded-none bg-[#15803D] hover:bg-[#166534] font-bold uppercase tracking-wider whitespace-nowrap">
                <Plus className="w-4 h-4 mr-1.5" /> Log Training Test
              </Button>
            )}
          </div>

          {trainings.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#27272A]">
              <Timer className="w-10 h-10 mx-auto text-[#3f3f46] mb-3" />
              <p className="text-[#A1A1AA] font-display text-2xl uppercase font-bold">No training data</p>
              <p className="text-[#71717A] text-sm mt-1">Log a 20 yd sprint, 5-10-5 shuttle and more to track athletic progress.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {TRAINING_FIELDS.filter((f) => trainBest[f.k] != null).map((f) => (
                  <div key={f.k} className="bg-[#141414] border border-[#27272A] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717A] leading-tight">{f.l}</p>
                    <p className="font-mono-metric text-2xl font-bold mt-2" style={{ color: f.color }}>{trainBest[f.k]}</p>
                    <p className="text-[10px] text-[#71717A] mt-1">{f.lowerBetter ? "Best (lowest)" : "Best (highest)"}</p>
                  </div>
                ))}
              </div>

              <div className="bg-[#141414] border border-[#27272A] p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A]">Progress Over Time</p>
                  <Select value={trainMetric} onValueChange={setTrainMetric}>
                    <SelectTrigger data-testid="training-metric-select" className="w-52 h-9 rounded-none bg-[#0A0A0A] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#27272A] rounded-none max-h-72">
                      {TRAINING_FIELDS.map((f) => <SelectItem key={f.k} value={f.k}>{f.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trainChartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                    <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 0, color: "#fff" }} />
                    <Line type="monotone" dataKey="value" stroke={trainField.color} strokeWidth={2.5} dot={{ r: 3, fill: trainField.color }} activeDot={{ r: 5 }} isAnimationActive />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-[#71717A] mt-2">
                  {trainField.lowerBetter ? "Lower is better — a downward trend means improvement." : "Higher is better — an upward trend means improvement."}
                </p>
              </div>

              <div className="bg-[#141414] border border-[#27272A] overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#27272A] hover:bg-transparent">
                      {["Date", ...TRAINING_FIELDS.map((f) => f.l), ...(isAdmin ? [""] : [])].map((h, hi) => (
                        <TableHead key={`${h}-${hi}`} className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...trainings].reverse().map((t) => (
                      <TableRow key={t.id} data-testid={`training-row-${t.id}`} className="border-[#27272A] font-mono-metric text-sm hover:bg-[#0A0A0A]">
                        <TableCell className="whitespace-nowrap font-sans">{t.date}</TableCell>
                        {TRAINING_FIELDS.map((f) => (
                          <TableCell key={f.k}>{t[f.k] ?? "—"}</TableCell>
                        ))}
                        {isAdmin && (
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <button data-testid={`edit-training-${t.id}`} onClick={() => openEditTraining(t)} className="text-[#71717A] hover:text-[#007AFF] transition-colors">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button data-testid={`delete-training-${t.id}`} onClick={() => deleteTraining(t.id)} className="text-[#71717A] hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <EntryForm open={showEntry} onOpenChange={setShowEntry} playerId={id} entry={editingEntry} onSaved={load} />
      <TrainingForm open={showTraining} onOpenChange={setShowTraining} playerId={id} test={editingTraining} onSaved={load} />
      <PlayerForm open={showEdit} onOpenChange={setShowEdit} player={player} onSaved={load} />
      <PlayerForm open={showBio} onOpenChange={setShowBio} player={player} bioOnly onSaved={load} />
      {player && <InviteDialog open={showInvite} onOpenChange={setShowInvite} player={player} />}
    </Layout>
  );
}
